import { EditorialSpreadPublicationControlsV1 } from "./EditorialSpreadPublicationControlsV1";
import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { mediaRecordUrl, resolveMediaUrl } from "@/lib/editorialMedia.mjs";
import { editorialImageStyle } from "@/lib/editorialReaderLayout.mjs";
import { MediaLibraryPickerV2, MEDIA_UPLOAD } from "./EditorialMediaPickerV2";
import { EditorialCropV2 } from "./EditorialCropV2";
import { PublicationDestinationV2 } from "./PublicationDestinationV2";

const MACRO_BG = "#EFE6D5";
const EYEBROW_COLOR = "#8A6A46";
const HEADLINE_COLOR = "#2A1D14";
const THOUGHT_COLOR = "#3E2E20";
const SIGNATURE_COLOR = "#8A6A46";
const CAPTION_COLOR = "#5B4326";
const CROP_ASPECT = "4/5";

function normalizeImage(raw) {
  if (!raw || (!raw.media_id && !raw.url)) return null;
  return {
    media_id: raw.media_id || null,
    url: raw.url || null,
    alt: raw.alt || "",
    focal_x: raw.focal_x ?? 0.5,
    focal_y: raw.focal_y ?? 0.5,
    zoom: raw.zoom ?? 1,
  };
}

function deriveInitialMacro(content) {
  const c = content || {};
  const m = c.macro && typeof c.macro === "object" ? c.macro : {};
  return {
    eyebrow: m.eyebrow ?? "",
    headline: m.headline ?? c.headline ?? "",
    thought: m.thought ?? c.body ?? "",
    signature: m.signature ?? c.signature ?? "",
    caption: m.caption ?? c.caption ?? "",
    mainImage: normalizeImage(m.main_image),
    mobileImage: normalizeImage(m.mobile_image),
  };
}

function MacroText({ eyebrow, headline, thought, signature, centered = false }) {
  return (
    <div className={centered ? "text-center" : ""}>
      {eyebrow && (
        <div className="text-[10px] uppercase tracking-[0.32em]" style={{ color: EYEBROW_COLOR }}>
          {eyebrow}
        </div>
      )}
      {headline && (
        <div className="mt-2 font-serif text-xl leading-[1.15]" style={{ color: HEADLINE_COLOR }}>
          {headline}
        </div>
      )}
      {thought && (
        <div
          className={(eyebrow || headline ? "mt-4 " : "") + "font-serif text-sm italic leading-[1.55]"}
          style={{ color: THOUGHT_COLOR }}
        >
          {thought}
        </div>
      )}
      {signature && (
        <div className="mt-4 text-[11px] uppercase tracking-[0.24em]" style={{ color: SIGNATURE_COLOR }}>
          — {signature}
        </div>
      )}
    </div>
  );
}

function PhotoField({
  label, helper, image, uploading, onUpload, onLibrary, onCrop, onRemove, onAltChange, testidPrefix,
}) {
  return (
    <div>
      <label className="mb-1 block text-[11px] text-espresso/65">{label}</label>
      {helper && <p className="mb-2 text-xs text-espresso/50">{helper}</p>}
      {image?.url ? (
        <div className="flex items-start gap-3">
          <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-espresso/10">
            <img
              src={resolveMediaUrl(image.url)}
              alt={image.alt || ""}
              className="h-full w-full object-cover"
              style={{ objectPosition: (image.focal_x * 100) + "% " + (image.focal_y * 100) + "%" }}
            />
          </div>
          <div className="flex-1 space-y-1.5">
            <input
              value={image.alt}
              onChange={(e) => onAltChange(e.target.value)}
              placeholder="Описание изображения (alt)"
              className="w-full rounded-lg border border-espresso/20 px-2 py-1 text-xs"
              data-testid={testidPrefix + "-alt"}
            />
            <div className="flex flex-wrap gap-1.5">
              <button type="button" onClick={onUpload} disabled={uploading}
                className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso disabled:opacity-50"
                data-testid={testidPrefix + "-replace"}>
                {uploading ? "Загрузка…" : "Заменить фото"}
              </button>
              <button type="button" onClick={onLibrary}
                className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso"
                data-testid={testidPrefix + "-library"}>
                Из Медиатеки
              </button>
              <button type="button" onClick={onCrop}
                className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso"
                data-testid={testidPrefix + "-crop"}>
                Настроить кадр
              </button>
              <button type="button" onClick={onRemove}
                className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso/70"
                data-testid={testidPrefix + "-remove"}>
                Удалить фото
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={onUpload} disabled={uploading}
            className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso disabled:opacity-50"
            data-testid={testidPrefix + "-upload"}>
            {uploading ? "Загрузка…" : "Загрузить фото"}
          </button>
          <button type="button" onClick={onLibrary}
            className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso"
            data-testid={testidPrefix + "-library-empty"}>
            Из Медиатеки
          </button>
        </div>
      )}
    </div>
  );
}

export function EditorialMacroV2({ block, volume, onClose, onSaved, publicationDirty, onPublicationInput }) {
  const c = block.content || {};
  const initial = deriveInitialMacro(c);

  const [eyebrow, setEyebrow] = useState(initial.eyebrow);
  const [headline, setHeadline] = useState(initial.headline);
  const [thought, setThought] = useState(initial.thought);
  const [signature, setSignature] = useState(initial.signature);
  const [caption, setCaption] = useState(initial.caption);
  const [mainImage, setMainImage] = useState(initial.mainImage);
  const [mobileImage, setMobileImage] = useState(initial.mobileImage);
  const [librarySlot, setLibrarySlot] = useState(null);
  const [cropSlot, setCropSlot] = useState(null);
  const [uploadingMain, setUploadingMain] = useState(false);
  const [uploadingMobile, setUploadingMobile] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [saveState, setSaveState] = useState("idle");
  const mainInputRef = useRef(null);
  const mobileInputRef = useRef(null);

  useEffect(() => {
    const next = deriveInitialMacro(block.content || {});
    setEyebrow(next.eyebrow);
    setHeadline(next.headline);
    setThought(next.thought);
    setSignature(next.signature);
    setCaption(next.caption);
    setMainImage(next.mainImage);
    setMobileImage(next.mobileImage);
    setLibrarySlot(null);
    setCropSlot(null);
    setSaveState("idle");
  }, [block.id]);  const activeMobileImage = mobileImage?.url ? mobileImage : mainImage;
  const cropImage = cropSlot === "mobile" ? mobileImage : cropSlot === "main" ? mainImage : null;

  const pickFromLibrary = (media) => {
    const img = normalizeImage({ media_id: media.id, url: mediaRecordUrl(media) || media.file_url || media.url || null });
    if (librarySlot === "mobile") setMobileImage(img);
    else setMainImage(img);
    setLibrarySlot(null);
  };

  const uploadTo = async (file, slot) => {
    if (!file) return;
    const setUploading = slot === "mobile" ? setUploadingMobile : setUploadingMain;
    const setImage = slot === "mobile" ? setMobileImage : setMainImage;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(MEDIA_UPLOAD, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImage(normalizeImage({
        media_id: data.id,
        url: mediaRecordUrl(data) || data.file_url || data.url || null,
      }));
      toast.success("Фото загружено");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const handleFile = (e, slot) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    uploadTo(file, slot);
  };

  const saveCrop = (values) => {
    if (cropSlot === "mobile") setMobileImage((img) => img ? { ...img, ...values } : img);
    if (cropSlot === "main") setMainImage((img) => img ? { ...img, ...values } : img);
    setCropSlot(null);
  };

  const save = async () => {
    setSaveState("saving");
    try {
      const nextContent = {
        ...c,
        macro: {
          eyebrow,
          headline,
          thought,
          signature,
          caption,
          main_image: mainImage ? { ...mainImage } : null,
          mobile_image: mobileImage ? { ...mobileImage } : null,
        },
        // Keep the V2 gallery's established media-id bridge coherent for old generic MACRO drafts.
        media_ids: mainImage?.media_id ? [mainImage.media_id] : [],
      };
      await api.patch("/admin/editorial/blocks/" + block.id, { content: nextContent });
      setSaveState("saved");
      const savedMediaItems = [mainImage, mobileImage]
        .filter((img) => img?.media_id && img?.url)
        .map((img) => ({ id: img.media_id, url: img.url }));
      onSaved?.({ ...block, content: nextContent }, savedMediaItems);
      toast.success("Изменения сохранены");
      setTimeout(() => setSaveState("idle"), 1500);
    } catch (e) {
      setSaveState("error");
      toast.error(formatApiError(e));
    }
  };

  const saveLabel = {
    idle: "", saving: "Сохранение…", saved: "Сохранено", error: "Ошибка сохранения",
  }[saveState];

  const PhotoPreview = ({ image, maxHeight }) => (
    <div className="relative overflow-hidden bg-[#E7DDCC]" style={{ aspectRatio: CROP_ASPECT, maxHeight }}>
      <img
        src={resolveMediaUrl(image.url)}
        alt={image.alt || headline || ""}
        className="absolute inset-0 h-full w-full"
        style={editorialImageStyle(image)}
      />
    </div>
  );

  return (
    <div onInputCapture={onPublicationInput} onChangeCapture={onPublicationInput}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="editorial-v2-macro-workspace">
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl bg-cream">
        <div className="flex items-start justify-between gap-3 border-b border-espresso/10 px-5 py-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-espresso/55">Макро-кадр</div>
            <div className="mt-0.5 truncate font-serif text-xl text-espresso">{headline || "Макро-кадр"}</div>
          </div>
          {saveLabel && (
            <span className="shrink-0 pt-1 text-[11px] uppercase tracking-widest text-espresso/50"
              data-testid="editorial-v2-macro-save-state">{saveLabel}</span>
          )}
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
          <PublicationDestinationV2 materialId={block.material_id} volume={volume} />

          <PhotoField
            label="Основное фото"
            image={mainImage}
            uploading={uploadingMain}
            onUpload={() => mainInputRef.current?.click()}
            onLibrary={() => setLibrarySlot("main")}
            onCrop={() => setCropSlot("main")}
            onRemove={() => setMainImage(null)}
            onAltChange={(v) => setMainImage((img) => img ? { ...img, alt: v } : img)}
            testidPrefix="editorial-v2-macro-main"
          />
          <input ref={mainInputRef} type="file" accept="image/*" className="hidden"
            onChange={(e) => handleFile(e, "main")} data-testid="editorial-v2-macro-main-file-input" />

          <PhotoField
            label="Мобильное фото · необязательно"
            helper="Если не добавлено, на телефоне используется основное фото."
            image={mobileImage}
            uploading={uploadingMobile}
            onUpload={() => mobileInputRef.current?.click()}
            onLibrary={() => setLibrarySlot("mobile")}
            onCrop={() => setCropSlot("mobile")}
            onRemove={() => setMobileImage(null)}
            onAltChange={(v) => setMobileImage((img) => img ? { ...img, alt: v } : img)}
            testidPrefix="editorial-v2-macro-mobile"
          />
          <input ref={mobileInputRef} type="file" accept="image/*" className="hidden"
            onChange={(e) => handleFile(e, "mobile")} data-testid="editorial-v2-macro-mobile-file-input" />

          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-[11px] text-espresso/60">Метка раздела</label>
              <input value={eyebrow} onChange={(e) => setEyebrow(e.target.value)}
                className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                data-testid="editorial-v2-macro-eyebrow" />
            </div>
            <div>
              <label className="mb-1 block text-[11px] text-espresso/60">Короткий заголовок</label>
              <input value={headline} onChange={(e) => setHeadline(e.target.value)}
                className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                data-testid="editorial-v2-macro-headline" />
            </div>
            <div>
              <label className="mb-1 block text-[11px] text-espresso/60">Короткая мысль</label>
              <textarea value={thought} onChange={(e) => setThought(e.target.value)} rows={4}
                className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                data-testid="editorial-v2-macro-thought" />
            </div>
            <div>
              <label className="mb-1 block text-[11px] text-espresso/60">Подпись / источник</label>
              <input value={signature} onChange={(e) => setSignature(e.target.value)}
                className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                data-testid="editorial-v2-macro-signature" />
            </div>
            <div>
              <label className="mb-1 block text-[11px] text-espresso/60">Подпись к фотографии</label>
              <input value={caption} onChange={(e) => setCaption(e.target.value)}
                className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                data-testid="editorial-v2-macro-caption" />
            </div>
          </div>

          {showPreview && (
            <div className="rounded-2xl border border-espresso/15 bg-white p-4" data-testid="editorial-v2-macro-preview">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-espresso/50">Предпросмотр разворота</div>
                <div className="flex gap-1 rounded-full border border-espresso/15 p-0.5 text-[11px]">
                  <button type="button" onClick={() => setPreviewDevice("desktop")}
                    className={previewDevice === "desktop" ? "rounded-full bg-espresso px-2.5 py-1 text-cream" : "rounded-full px-2.5 py-1 text-espresso/60"}>
                    Desktop
                  </button>
                  <button type="button" onClick={() => setPreviewDevice("mobile")}
                    className={previewDevice === "mobile" ? "rounded-full bg-espresso px-2.5 py-1 text-cream" : "rounded-full px-2.5 py-1 text-espresso/60"}>
                    Mobile
                  </button>
                </div>
              </div>

              {previewDevice === "desktop" ? (
                mainImage?.url ? (
                  <div className="mx-auto p-4" style={{ maxWidth: "1200px", backgroundColor: MACRO_BG }}>
                    <div className="grid items-center gap-3 lg:gap-4"
                      style={{ gridTemplateColumns: "minmax(0,62fr) minmax(0,38fr)" }}>
                      <div>
                        <PhotoPreview image={mainImage} maxHeight="680px" />
                        {caption && <div className="mt-3 max-w-[42ch] font-serif text-[12px] italic leading-[1.5]"
                          style={{ color: CAPTION_COLOR }}>{caption}</div>}
                      </div>
                      <MacroText eyebrow={eyebrow} headline={headline} thought={thought} signature={signature} />
                    </div>
                  </div>
                ) : (
                  <div className="mx-auto px-6 py-16 text-center" style={{ maxWidth: "720px", backgroundColor: MACRO_BG }}>
                    <MacroText eyebrow={eyebrow} headline={headline} thought={thought} signature={signature} centered />
                  </div>
                )
              ) : activeMobileImage?.url ? (
                <div className="mx-auto max-w-[240px] p-3" style={{ backgroundColor: MACRO_BG }}>
                  <PhotoPreview image={activeMobileImage} maxHeight="420px" />
                  {caption && <div className="mt-3 font-serif text-[12px] italic leading-[1.5]"
                    style={{ color: CAPTION_COLOR }}>{caption}</div>}
                  <div className="mt-3">
                    <MacroText eyebrow={eyebrow} headline={headline} thought={thought} signature={signature} />
                  </div>
                </div>
              ) : (
                <div className="mx-auto max-w-[240px] px-4 py-8 text-center" style={{ backgroundColor: MACRO_BG }}>
                  <MacroText eyebrow={eyebrow} headline={headline} thought={thought} signature={signature} centered />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-espresso/10 px-5 py-4">
          <EditorialSpreadPublicationControlsV1 block={block} dirty={publicationDirty} />
          <button type="button" onClick={onClose}
            className="rounded-xl border border-espresso/20 bg-white px-4 py-2 text-sm text-espresso">
            Закрыть
          </button>
          <button type="button" onClick={() => setShowPreview((v) => !v)}
            className="rounded-xl border border-espresso/20 bg-white px-4 py-2 text-sm text-espresso">
            {showPreview ? "Скрыть предпросмотр" : "Предпросмотр разворота"}
          </button>
          <button type="button" onClick={save} disabled={saveState === "saving"}
            className="rounded-xl bg-espresso px-4 py-2 text-sm text-cream disabled:opacity-50">
            Сохранить изменения
          </button>
        </div>
      </div>

      <MediaLibraryPickerV2
        open={librarySlot !== null}
        onClose={() => setLibrarySlot(null)}
        onPick={pickFromLibrary}
      />

      <EditorialCropV2
        open={cropSlot !== null}
        url={resolveMediaUrl(cropImage?.url || "")}
        aspectRatio={CROP_ASPECT}
        frameLabel={cropSlot === "mobile" ? "Мобильное фото · 4:5" : "Основное фото · 4:5"}
        focalX={cropImage?.focal_x ?? 0.5}
        focalY={cropImage?.focal_y ?? 0.5}
        zoom={cropImage?.zoom ?? 1}
        onSave={saveCrop}
        onClose={() => setCropSlot(null)}
      />
    </div>
  );
}
